import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';
import teacherAssignmentsService from '../../services/teacherAssignmentsService';
import RubricGrader from '../../components/grading/RubricGrader';
import { QuestionRenderer } from '../../components/assignments/QuestionRenderer';
import { CheckCircle, X, CaretLeft, CaretRight } from '@phosphor-icons/react';
import './SubmissionGradingPanel.css';

function SubmissionGradingPanel({
  assignmentId,
  submissions,
  onClose,
  onGradingComplete,
  assignmentFetchUrl,
  gradePathResolver,
  initialSubmissionIdx = 0,
}) {
  const [currentSubmissionIdx, setCurrentSubmissionIdx] = useState(initialSubmissionIdx);
  const [feedback, setFeedback] = useState('');
  const [gradedSubmissions, setGradedSubmissions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [questionScores, setQuestionScores] = useState({});
  const [manualOverrides, setManualOverrides] = useState({});

  const loadAssignment = React.useCallback(async () => {
    try {
      const data = assignmentFetchUrl
        ? await api.get(assignmentFetchUrl)
        : await teacherAssignmentsService.getAssignment(assignmentId);
      const loadedAssignment = data.assignment || data;
      console.log('Loaded assignment:', loadedAssignment);
      console.log('Assignment questions:', loadedAssignment?.questions);
      setAssignment(loadedAssignment);
    } catch (error) {
      console.error('Failed to load assignment:', error);
      toast.error('Failed to load assignment');
    }
  }, [assignmentId, assignmentFetchUrl]);

  const loadSubmissionDetails = React.useCallback(() => {
    const submission = submissions[currentSubmissionIdx];
    if (submission && assignment?.questions) {
      setFeedback(submission.grade?.feedback || submission.feedback || '');
      
      // Check if submission is already graded and has graded_answers
      if ((submission.status === 'graded' || submission.grade) && submission.graded_answers) {
        // Load existing graded scores
        setQuestionScores(submission.graded_answers);
        setManualOverrides(Object.keys(submission.graded_answers).reduce((acc, qid) => {
          acc[qid] = true; // Mark as manually graded
          return acc;
        }, {}));
      } else {
        // Initialize question scores and auto-grade
        const initialScores = {};
        const initialOverrides = {};
        
        assignment.questions.forEach((question) => {
          const studentAnswer = getStudentAnswer(question.id);
          const autoScore = autoGradeQuestion(question, studentAnswer);
          initialScores[question.id] = autoScore;
          initialOverrides[question.id] = false;
        });
        
        setQuestionScores(initialScores);
        setManualOverrides(initialOverrides);
      }
      
      // Check if already graded
      if (submission.graded_date) {
        try {
          if (submission.rubric_scores) {
            const rubricData = JSON.parse(submission.rubric_scores);
            setGradedSubmissions(prev => ({
              ...prev,
              [submission.id]: rubricData
            }));
          }
        } catch (e) {
          console.log('Failed to parse rubric scores');
        }
      }
    }
  }, [assignment, currentSubmissionIdx, submissions]);

  // Load assignment details
  useEffect(() => {
    loadAssignment();
  }, [assignmentId, loadAssignment]);

  // Reset index when initialSubmissionIdx changes
  useEffect(() => {
    setCurrentSubmissionIdx(initialSubmissionIdx);
  }, [initialSubmissionIdx]);

  // Load current submission details
  useEffect(() => {
    loadSubmissionDetails();
  }, [currentSubmissionIdx, submissions, assignment, loadSubmissionDetails]);

  const getStudentAnswer = (questionId) => {
    const currentSubmission = submissions[currentSubmissionIdx];
    if (!currentSubmission?.submission_text) return null;
    
    try {
      const parsed = JSON.parse(currentSubmission.submission_text);
      const answers = parsed.answers || parsed;
      return answers[questionId] ?? currentSubmission.submission_text;
    } catch (e) {
      return currentSubmission.submission_text;
    }
  };

  const autoGradeQuestion = (question, studentAnswer) => {
    if (!studentAnswer) return 0;
    
    const questionType = question.type;
    const correctAnswer = question.answer;
    
    switch (questionType) {
      case 'multipleChoice':
        return studentAnswer === correctAnswer ? 1 : 0;
        
      case 'shortAnswer':
        // Simple fuzzy matching for short answers
        if (!correctAnswer) return 0;
        const studentLower = studentAnswer.toLowerCase().trim();
        const correctLower = correctAnswer.toLowerCase().trim();
        return studentLower === correctLower ? 1 : 0;
        
      case 'matching':
        // Check if all matches are correct
        if (!correctAnswer || typeof studentAnswer !== 'object') return 0;
        try {
          const correctMatches = JSON.parse(correctAnswer);
          let correctCount = 0;
          let totalCount = Object.keys(correctMatches).length;
          
          Object.entries(studentAnswer).forEach(([key, value]) => {
            if (correctMatches[key] === value) {
              correctCount++;
            }
          });
          
          return totalCount > 0 ? correctCount / totalCount : 0;
        } catch (e) {
          return 0;
        }
        
      case 'essay':
        // Essays require manual grading
        return 0;
        
      default:
        return 0;
    }
  };

  const handleQuestionScoreChange = (questionId, score) => {
    setQuestionScores(prev => ({
      ...prev,
      [questionId]: score
    }));
    setManualOverrides(prev => ({
      ...prev,
      [questionId]: true
    }));
  };

  const getTotalScore = () => {
    if (!assignment?.questions) return 0;
    
    const totalPossible = assignment.questions.length;
    const totalEarned = Object.values(questionScores).reduce((sum, score) => sum + score, 0);
    const pointsPossible = assignment.points_possible ?? assignment.max_score ?? 100;
    
    return (totalEarned / totalPossible) * pointsPossible;
  };

  const handleSubmitGrade = async () => {
    const submission = submissions[currentSubmissionIdx];
    const totalScore = getTotalScore();
    
    const pointsPossible = assignment?.points_possible ?? assignment?.max_score ?? 100;
    if (totalScore < 0 || totalScore > pointsPossible) {
      toast.error(`Score must be between 0 and ${pointsPossible}`);
      return;
    }

    setSubmitting(true);
    try {
      const gradeData = {
        score: parseFloat(totalScore),
        feedback: feedback.trim() || undefined,
        question_scores: questionScores,
        manual_overrides: manualOverrides
      };

      if (gradePathResolver) {
        await api.post(gradePathResolver(submission), gradeData);
      } else {
        await teacherAssignmentsService.gradeSubmission(assignmentId, submission.id, gradeData);
      }

      toast.success('Submission graded successfully');
      
      // Mark as graded locally
      setGradedSubmissions(prev => ({
        ...prev,
        [submission.id]: { graded: true }
      }));

      // Move to next ungraded submission if available
      let nextIdx = currentSubmissionIdx + 1;
      while (nextIdx < submissions.length && gradedSubmissions[submissions[nextIdx].id]) {
        nextIdx++;
      }

      if (nextIdx < submissions.length) {
        setCurrentSubmissionIdx(nextIdx);
      }

      if (onGradingComplete) {
        onGradingComplete();
      }
    } catch (error) {
      toast.error('Failed to grade submission');
    } finally {
      setSubmitting(false);
    }
  };

  if (!submissions || submissions.length === 0) {
    return (
      <div className="grading-panel-empty">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>No submissions to grade</p>
        </div>
      </div>
    );
  }

  const currentSubmission = submissions[currentSubmissionIdx];
  const isGraded = currentSubmission?.status === 'graded' || !!gradedSubmissions[currentSubmission?.id];
  const totalScore = getTotalScore();

  return (
    <div className="grading-panel-overlay">
      <div className="grading-panel">
        {/* Header */}
        <div className="grading-panel-header">
          <div>
            <h2>Grade Submission</h2>
            <p>
              {currentSubmissionIdx + 1} of {submissions.length} • {currentSubmission.student_name}
            </p>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
              Submitted: {new Date(currentSubmission.submission_date).toLocaleString()}
              {isGraded && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  marginLeft: '12px',
                  padding: '2px 8px',
                  backgroundColor: '#dcfce7',
                  color: '#166534',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  <CheckCircle size={14} /> Graded
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="nav-btn"
            style={{ border: 'none', background: 'none', color: '#6b7280', padding: '8px' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="grading-panel-content">
          <div className="submission-view">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Question Review</h3>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Total Score: <strong>{totalScore.toFixed(1)} / {(assignment?.points_possible ?? assignment?.max_score ?? 100)}</strong>
              </div>
            </div>

            {assignment?.questions && assignment.questions.length > 0 ? (
              <div className="submission-answers">
                {assignment.questions.map((question, idx) => {
                  const studentAnswer = getStudentAnswer(question.id);
                  const autoScore = autoGradeQuestion(question, studentAnswer);
                  const currentScore = questionScores[question.id] || 0;
                  const isManuallyGraded = manualOverrides[question.id];
                  const needsManualGrading = ['essay', 'shortAnswer'].includes(question.type) && autoScore === 0;
                  
                  return (
                    <div key={question.id} className="question-grading-item">
                      <div className="question-header">
                        <div className="question-meta">
                          <span className="question-number">Q{idx + 1}</span>
                          <span className="question-type">{question.type}</span>
                          {needsManualGrading && (
                            <span className="manual-grade-badge">Manual Grading Required</span>
                          )}
                        </div>
                        <div className="question-score">
                          <input
                            type="number"
                            min="0"
                            max="1"
                            step="0.1"
                            value={currentScore}
                            onChange={(e) => handleQuestionScoreChange(question.id, parseFloat(e.target.value) || 0)}
                            className="score-input"
                            title="Score (0-1)"
                            disabled={isGraded}
                          />
                          <span>/ 1.0</span>
                          {isManuallyGraded && <span className="manual-indicator">Manual</span>}
                        </div>
                      </div>
                      
                      <div className="question-content">
                        <p className="question-text">{question.question}</p>
                        
                        <div className="answer-comparison">
                          <div className="correct-answer">
                            <label>Correct Answer:</label>
                            <div className="answer-content">
                              {renderCorrectAnswer(question)}
                            </div>
                          </div>
                          
                          <div className="student-answer">
                            <label>Student Answer:</label>
                            <div className="answer-content">
                              {renderStudentAnswer(question, studentAnswer)}
                            </div>
                          </div>
                          
                          <div className="grading-result">
                            <div className={`auto-score ${autoScore === 1 ? 'correct' : autoScore === 0 ? 'incorrect' : 'partial'}`}>
                              Auto Score: {autoScore.toFixed(1)}
                              {autoScore === 1 && <CheckCircle size={16} />}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                <p>Loading assignment questions...</p>
              </div>
            )}
          </div>

          {/* Grading Section */}
          <div className="grading-section">
            <h3>Final Grade & Feedback</h3>

            <div className="final-grade-summary">
              <div className="grade-breakdown">
                <div className="total-score">
                  <span className="label">Total Score:</span>
                  <span className="value">{totalScore.toFixed(1)} / {(assignment?.points_possible ?? assignment?.max_score ?? 100)}</span>
                </div>
                <div className="percentage">
                  <span className="label">Percentage:</span>
                  <span className="value">
                    {((totalScore / (assignment?.points_possible ?? assignment?.max_score ?? 100)) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '8px'
            }}>
              Feedback
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              disabled={isGraded}
              placeholder="Add feedback for the student..."
              maxLength={500}
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />

            <div className="grading-actions">
              <button
                onClick={handleSubmitGrade}
                disabled={submitting || isGraded}
                className="submit-grade-btn"
              >
                {submitting ? 'Submitting...' : isGraded ? 'Already Graded' : 'Submit Grade'}
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="grading-panel-footer">
          <button
            onClick={() => setCurrentSubmissionIdx(Math.max(0, currentSubmissionIdx - 1))}
            disabled={currentSubmissionIdx === 0}
            className="nav-btn"
          >
            <CaretLeft size={20} />
            Previous
          </button>
          
          <span className="submission-counter">
            {currentSubmissionIdx + 1} of {submissions.length}
          </span>
          
          <button
            onClick={() => setCurrentSubmissionIdx(Math.min(submissions.length - 1, currentSubmissionIdx + 1))}
            disabled={currentSubmissionIdx === submissions.length - 1}
            className="nav-btn"
          >
            Next
            <CaretRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}

function renderCorrectAnswer(question) {
  const { type, answer, options } = question;
  
  switch (type) {
    case 'multipleChoice':
      return <strong>{answer || 'Not specified'}</strong>;
      
    case 'shortAnswer':
      return <strong>{answer || 'Not specified'}</strong>;
      
    case 'matching':
      if (answer) {
        try {
          const matches = JSON.parse(answer);
          return (
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {Object.entries(matches).map(([key, value]) => (
                <li key={key}>{key} → {value}</li>
              ))}
            </ul>
          );
        } catch (e) {
          return <span>{answer}</span>;
        }
      }
      return <span>Not specified</span>;
      
    case 'essay':
      return <em>Manual grading required</em>;
      
    default:
      return <span>{answer || 'Not specified'}</span>;
  }
}

function renderStudentAnswer(question, studentAnswer) {
  const { type } = question;
  
  if (!studentAnswer) {
    return <em style={{ color: '#9ca3af' }}>Not answered</em>;
  }
  
  switch (type) {
    case 'multipleChoice':
      return <strong>{studentAnswer}</strong>;
      
    case 'shortAnswer':
      return <span>{studentAnswer}</span>;
      
    case 'matching':
      if (typeof studentAnswer === 'object') {
        return (
          <ul style={{ margin: 0, paddingLeft: '16px' }}>
            {Object.entries(studentAnswer).map(([key, value]) => (
              <li key={key}>{key} → {value}</li>
            ))}
          </ul>
        );
      }
      return <span>{JSON.stringify(studentAnswer)}</span>;
      
    case 'essay':
      return <span>{studentAnswer}</span>;
      
    default:
      return <span>{studentAnswer}</span>;
  }
}

export default SubmissionGradingPanel;